<template>
  <div class="calendar-week">
    <div class="calendar-week__grid">
      <div
        v-for="(day, idx) in weekDays"
        :key="day.key"
        class="calendar-week__column"
      >
        <div class="calendar-week__header">
          {{ day.name }}
        </div>

        <div
          class="calendar-week__cell"
          :class="[isWeekend(idx) ? 'calendar-week__cell--weekend' : '']"
        >
          <div
            v-for="(appointment, appointmentIndex) in getAppointmentsByDay(
              day.date
            )"
            :key="appointmentKey(appointment, appointmentIndex)"
            class="calendar-week__card"
            :style="appointmentStyle(appointment)"
            role="button"
            tabindex="0"
            @click="$emit('select-appointment', appointment)"
            @keyup.enter="$emit('select-appointment', appointment)"
          >
            <div class="calendar-week__student">
              <span class="calendar-week__student-name">
                {{ studentFullName(appointment) }}
              </span>
              <span
                v-if="studentSie(appointment)"
                class="calendar-week__student-sie"
              >
                {{ studentSie(appointment) }}
              </span>
            </div>

            <div class="calendar-week__title">
              <i class="fas fa-folder-open mr-1" />
              <span>{{ appointmentTitle(appointment) }}</span>
            </div>

            <div
              v-if="appointment.compuAppointmentNumber"
              class="calendar-week__appointment-number"
            >
              {{ appointment.compuAppointmentNumber }}
            </div>

            <div class="calendar-week__time">
              <i
                class="fas fa-clock mr-1"
                :style="{ color: getAppointmentColor(appointment) }"
              />
              {{ formatAppointmentTime(appointment.startTime) }} -
              {{ formatAppointmentTime(appointment.endTime) }}
            </div>

            <div
              class="calendar-week__divider"
              :style="{
                backgroundColor: bgWithAlpha(
                  getAppointmentColor(appointment),
                  0.35
                ),
              }"
            />

            <div class="calendar-week__actions">
              <span
                class="calendar-week__citation"
                :style="citationStyle(appointment)"
              >
                <i class="fas fa-calendar-alt mr-1" />
                {{ citationLabel(appointment) }}
              </span>

              <span class="calendar-week__status">
                <span
                  class="calendar-week__status-dot"
                  :style="{ backgroundColor: getStatusDotColor(appointment) }"
                />
                {{ appointment.statusName || 'Coordinada' }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment';
import calendarAppointmentDisplayMixin from '../mixins/calendarAppointmentDisplayMixin';

export default {
  name: 'CompuCalendarModeWeek',
  mixins: [calendarAppointmentDisplayMixin],
  props: {
    value: { type: Array, default: () => [] },
    categories: { type: Array, default: () => [] },
    currentDate: { type: Date, required: true, default: () => new Date() },
  },
  computed: {
    model() {
      return this.value;
    },
    weekDays() {
      const start = moment(this.currentDate)
        .startOf('day')
        .subtract(moment(this.currentDate).day(), 'days');
      const names = [
        'Domingo',
        'Lunes',
        'Martes',
        'Miércoles',
        'Jueves',
        'Viernes',
        'Sábado',
      ];

      return names.map((name, index) => {
        const date = moment(start).add(index, 'days');

        return {
          name,
          date,
          key: date.format('YYYY-MM-DD'),
        };
      });
    },
  },
  methods: {
    isWeekend(idx) {
      return idx === 0 || idx === 6;
    },
    getAppointmentsByDay(date) {
      return this.model.filter((appointment) =>
        moment(appointment.appointmentDate).isSame(date, 'day')
      );
    },
  },
};
</script>

<style scoped>
.calendar-week {
  min-height: 520px;
  position: relative;
}

.calendar-week__grid {
  display: grid;
  grid-template-columns: repeat(7, minmax(120px, 1fr));
  gap: 10px;
  overflow-x: auto;
  min-height: 560px;
}

.calendar-week__column {
  display: flex;
  flex-direction: column;
  min-width: 120px;
}

.calendar-week__header {
  background-color: #d9e5df !important;
  border-radius: 4px;
  color: #11291b;
  font-weight: 700;
  margin-bottom: 10px;
  padding: 10px 8px;
  text-align: center;
}

.calendar-week__cell {
  flex: 1;
  min-height: 520px;
  background-color: #f4f6f3;
  border-radius: 4px;
  padding: 10px;
}

.calendar-week__cell--weekend {
  background-color: #e2e2e2;
}

.calendar-week__card {
  border-radius: 4px;
  cursor: pointer;
  margin-bottom: 10px;
  min-height: 160px;
  padding: 10px;
}

.calendar-week__card:focus {
  outline: 2px solid #2db782;
  outline-offset: 2px;
}

.calendar-week__student {
  color: #11291b;
  display: flex;
  flex-direction: column;
  font-size: 12px;
  font-weight: 700;
  line-height: 1.25;
  margin-bottom: 8px;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
}

.calendar-week__student-name {
  overflow: hidden;
  text-overflow: ellipsis;
}

.calendar-week__student-sie {
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
}

.calendar-week__title {
  display: flex;
  align-items: center;
  font-size: 12px;
  font-weight: 600;
  min-width: 0;
}

.calendar-week__title span {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.calendar-week__appointment-number {
  color: #11291b;
  font-size: 12px;
  margin-top: 8px;
  white-space: nowrap;
}

.calendar-week__time {
  color: #11291b;
  font-size: 12px;
  margin-top: 10px;
  white-space: nowrap;
}

.calendar-week__divider {
  height: 1px;
  margin: 12px 0;
}

.calendar-week__actions {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
}

.calendar-week__citation {
  display: inline-flex;
  align-items: center;
  border-radius: 16px;
  color: white;
  font-size: 12px;
  font-weight: 700;
  padding: 6px 12px;
}

.calendar-week__status {
  display: inline-flex;
  align-items: center;
  background: white;
  color: #11291b;
  border-radius: 14px;
  font-size: 12px;
  font-weight: 700;
  padding: 5px 10px;
}

.calendar-week__status-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  margin-right: 6px;
}

@media (max-width: 1200px) {
  .calendar-week__grid {
    grid-template-columns: repeat(7, minmax(150px, 1fr));
  }
}

@media (max-width: 768px) {
  .calendar-week__grid {
    grid-template-columns: repeat(7, minmax(170px, 1fr));
  }
}
</style>
